/**
 * `/hr/employees/{id}` — la scheda di una persona: chi è, com'è inquadrata, cosa ha addosso.
 *
 * ## Perché sette schede e non quattro
 *
 * Prima erano Anagrafica (sola lettura, con una modale per modificare), Mansioni, Gerarchia e
 * un rimando alla pagina degli orari. Tutto il resto di una persona — impiego, turnazione,
 * patenti, fasce di trasferta — si gestiva altrove o non si gestiva affatto, e chi apriva la
 * scheda per rispondere a «questo può guidare il furgone?» o «che turno fa?» usciva a mani
 * vuote. Ora la scheda è il posto dove quelle domande hanno risposta.
 *
 * ## Un principio che vale per tutte le schede storiche
 *
 * Ruoli, turnazioni e fasce di trasferta **si chiudono con una data**, non si cancellano: le
 * righe passate spiegano decisioni già prese (un permesso concesso, una trasferta approvata
 * con quei tetti) e cancellarle lascia quelle decisioni senza motivo. L'eliminazione resta
 * solo per correggere un inserimento sbagliato, e lo dice ogni volta che la si preme.
 */
import { useCurrentTenantId } from '@/shared/hooks/useCurrentTenant';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Stack } from '@mantine/core';
import {
  IconCalendarRepeat, IconCar, IconClock, IconCreditCard, IconId, IconPlaneTilt, IconSitemap, IconUser,
  IconBriefcase, IconReceipt,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { PageLoader } from '@/shared/components/LoadingState';
import { MaeCard, MaePageHead, MaeShell, MaeTabPills } from '@/shared/desktop/MaeShell';
import { ExternalSyncNotice } from '@/features/admin/ExternalSyncNotice';
import { useMaePageTitle } from '@/shared/desktop/MaeShell.utils';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import { EmployeeHierarchyPanel } from '@/features/workforce/EmployeeHierarchyPanel';
import { fetchEmployeeRoles } from '@/features/workforce/employeeRolesApi';
import {
  EmpMasterDataTab, EmpEmploymentTab, EmpPositionCard, EmployeeIdentityHeader,
  type EmployeeDetail,
} from '@/features/workforce/EmployeeCoreTabs';
import { completeName } from '@/features/workforce/employeeDossier';
import {
  EmpLicensesTab, EmpRolesTab, EmpTripsTab, EmpRotationTab,
} from '@/features/workforce/EmployeeDossierTabs';
import { EmpSubscriptionsTab } from '@/features/subscriptions/EmpAbbonamentiTab';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { invalidateEmployeeLists } from '@/features/workforce/anagraficaDipendenti';
import { useEmployeeRights } from '@/features/workforce/employeeRights';
import { EmployeeExternalTab } from '@/features/workforce/EmployeeExternalTab';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { EmployeeHoursPanel } from '@/features/workforce/EmployeeHoursPanel';
import { EmployeeRotationAlerts } from '@/features/workforce/EmployeeRotationAlerts';

export const Route = createFileRoute('/_app/hr/employees/$employeeId')({ component: EmployeeDetailPage });

type Detail = 'anagrafica' | 'impiego' | 'esterno' | 'ruoli' | 'orari' | 'turnazione' | 'patenti' | 'trasferte' | 'abbonamenti' | 'gerarchia';

const TABS = [
  { value: 'anagrafica' as const, label: 'Anagrafica', icon: IconUser },
  { value: 'impiego' as const, label: 'Impiego', icon: IconBriefcase },
  /* Solo per i collaboratori esterni: si toglie dall'elenco più sotto per gli interni. */
  { value: 'esterno' as const, label: 'Dati esterno', icon: IconReceipt },
  { value: 'ruoli' as const, label: 'Storico mansioni', icon: IconId },
  { value: 'orari' as const, label: 'Orari', icon: IconClock },
  { value: 'turnazione' as const, label: 'Turnazione', icon: IconCalendarRepeat },
  { value: 'patenti' as const, label: 'Patenti', icon: IconCar },
  { value: 'trasferte' as const, label: 'Trasferte', icon: IconPlaneTilt },
  // Abbonamenti (BE 7 set 2026): stessa regola delle altre schede storiche, si chiude con una data.
  { value: 'abbonamenti' as const, label: 'Abbonamenti', icon: IconCreditCard },
  { value: 'gerarchia' as const, label: 'Gerarchia', icon: IconSitemap },
];

interface RoleLite extends Denormalized { type: 'erp-roles'; name?: string }

function EmployeeDetailPage() {
  const { employeeId } = useParams({ from: '/_app/hr/employees/$employeeId' });
  const qc = useQueryClient();
  const isMobile = useIsMobileShell();
  const [detail, setDetail] = useState<Detail>('anagrafica');

  const empQ = useQuery({
    queryKey: ['erp-employee', employeeId],
    queryFn: () => fetchOne<EmployeeDetail>('erp-employees', employeeId, {
      include: ['site', 'businessGroup', 'user', 'legalBasisConfirmedBy', 'tenant'],
    }),
  });

  /* Il ruolo in corso serve solo alla testata: è la riga senza data di fine. */
  const rolesQ = useQuery({ queryKey: ['emp-roles', employeeId], queryFn: () => fetchEmployeeRoles(employeeId) });
  const catalogQ = useQuery({
    queryKey: ['erp-roles-catalogo'], staleTime: 10 * 60_000,
    queryFn: () => fetchList<RoleLite>('erp-roles', { sort: ['name'], page: { size: 100 } }),
  });
  const currentRole = useMemo(() => {
    const attivo = (rolesQ.data?.data ?? []).find((r) => !r.endDate);
    if (!attivo) return null;
    return (catalogQ.data?.data ?? []).find((x) => x.id === attivo.role?.id)?.name ?? 'Mansione assegnata';
  }, [rolesQ.data, catalogQ.data]);

  const emp = empQ.data;
  /* L'azienda del dipendente, o quella in cui si è se il dato non c'è: la gerarchia si legge lì. */
  const currentCompanyId = useCurrentTenantId();
  const rights = useEmployeeRights(emp);
  const isExternal = (emp?.employeeType ?? 'internal') === 'external';
  const tabs = TABS.filter((t) => t.value !== 'esterno' || isExternal);
  const titolo = completeName(emp);
  const showTitle = useMaePageTitle(titolo);
  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['erp-employee', employeeId] });
    void qc.invalidateQueries({ queryKey: ['emp-roles', employeeId] });
    invalidateEmployeeLists(qc);
  };

  const body = (
    <>
      {empQ.isError && <QueryErrorAlert error={empQ.error} title="Dipendente non leggibile" />}
      {empQ.isLoading && <PageLoader label="Caricamento del dipendente…" />}

      {emp && (
        <>
          <EmployeeIdentityHeader emp={emp} role={currentRole} />
          <EmpPositionCard employeeId={employeeId} emp={emp} />

          {/* Come in Lares: chi non può modificare questo dipendente legge e basta, e lo sa. */}
          {!rights.canModify && (
            <MaeAdvice tone="info" title="Sola lettura">
              Non puoi modificare questo dipendente: serve il permesso di aggiornare i dipendenti, e per
              un’azienda diversa dalla tua quello di amministrare le controllate.
            </MaeAdvice>
          )}

          {/* Cessato con la turnazione aperta, o con un ciclo spento: sopra le schede, perché chi
              cessa una persona sta su «Impiego» e non apre «Turnazione». */}
          <EmployeeRotationAlerts emp={emp} name={titolo} />

          <MaeTabPills tabs={tabs} active={detail} onChange={setDetail} />

          {/* Un `fieldset` spento spegne i campi dentro: per chi non può modificare. */}
          <fieldset disabled={!rights.canModify} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            {detail === 'anagrafica' && <EmpMasterDataTab emp={emp} onSaved={reload} />}
            {detail === 'impiego' && <EmpEmploymentTab emp={emp} onSaved={reload} />}
          </fieldset>
          {detail === 'esterno' && isExternal && <EmployeeExternalTab employeeId={employeeId} canModify={rights.canModify} />}
          {detail === 'ruoli' && <EmpRolesTab employeeId={employeeId} />}

          {detail === 'orari' && <EmployeeHoursPanel emp={emp} canModify={rights.canModify} />}

          {detail === 'turnazione' && <EmpRotationTab employeeId={employeeId} />}
          {detail === 'patenti' && <EmpLicensesTab employeeId={employeeId} />}
          {detail === 'trasferte' && <EmpTripsTab employeeId={employeeId} />}
          {detail === 'abbonamenti' && <EmpSubscriptionsTab employeeId={employeeId} employeeName={titolo} />}

          {detail === 'gerarchia' && (
            <MaeCard>
              <EmployeeHierarchyPanel employeeId={employeeId} employeeName={titolo} companyId={emp?.tenant?.id ?? currentCompanyId} />
            </MaeCard>
          )}
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Stack gap={16} px={16} pt={16} pb={28}>
        <MobileHeader title={titolo} />
        {body}
      </Stack>
    );
  }

  return (
    <MaeShell>
      {showTitle && (
        <MaePageHead
          title={titolo}
          sub="Anagrafica, impiego, mansioni, orari, turnazione, patenti e trasferte di questa persona."
          icon={<IconUser size={23} />}
          tone="bordeaux"
        />
      )}
      {/* Fuori dal condizionale della testata: che l'anagrafica sia dell'ERP non dipende da
          quale intestazione si sta mostrando. */}
      <ExternalSyncNotice resourceType="erp-employees" records={[emp]} />
      {body}
    </MaeShell>
  );
}
